// ./src/utils/analytics/Analytics.ts
import { AnalyticsProvider, AnalyticsEventParams } from './types';

/** How long a "send, then leave" waits for the providers before leaving anyway. */
export const SEND_BEFORE_LEAVING_MS = 1000;

class Analytics {
  private providers: AnalyticsProvider[] = [];
  private debug = false;

  constructor(options?: { debug?: boolean }) {
    this.debug = options?.debug ?? false;
  }

  addProvider(provider: AnalyticsProvider): this {
    provider.initialize();
    this.providers.push(provider);

    if (this.debug) {
      console.log(`[Analytics] Provider added: ${provider.name}`);
    }

    return this;
  }

  removeProvider(name: string): this {
    this.providers = this.providers.filter((p) => p.name !== name);

    return this;
  }

  pageView(pageUrl: string, pageTitle: string, routePattern?: string | null): void {
    if (this.debug) {
      console.log(`[Analytics] pageView:`, { pageUrl, pageTitle, routePattern });
    }

    this.providers.forEach((provider) => {
      try {
        provider.pageView(pageUrl, pageTitle, routePattern);
      } catch (error) {
        console.error(`[Analytics] ${provider.name} pageView error:`, error);
      }
    });
  }

  sendEvent(eventName: string, params?: AnalyticsEventParams): void {
    if (this.debug) {
      console.log(`[Analytics] sendEvent:`, { eventName, params });
    }

    this.providers.forEach((provider) => {
      try {
        provider.sendEvent(eventName, params);
      } catch (error) {
        console.error(`[Analytics] ${provider.name} sendEvent error:`, error);
      }
    });
  }

  /**
   * Send an event the page is about to be LEFT after (a full redirect). Resolves once every provider has it, or after
   * `SEND_BEFORE_LEAVING_MS` — never later, so a blocked tracker cannot hold the visitor on the page.
   */
  sendEventBeforeLeaving(eventName: string, params?: AnalyticsEventParams): Promise<void> {
    if (this.debug) {
      console.log(`[Analytics] sendEventBeforeLeaving:`, { eventName, params });
    }

    const handed = this.providers.map(
      (provider) =>
        new Promise<void>((resolve) => {
          try {
            provider.sendEventThen(eventName, params, resolve);
          } catch (error) {
            console.error(`[Analytics] ${provider.name} sendEventThen error:`, error);
            resolve();
          }
        }),
    );
    const timeout = new Promise<void>((resolve) => {
      setTimeout(resolve, SEND_BEFORE_LEAVING_MS);
    });

    return Promise.race([Promise.all(handed).then(() => undefined), timeout]);
  }

  setUser(userId: string | null): void {
    if (this.debug) {
      console.log(`[Analytics] setUser:`, { userId });
    }

    this.providers.forEach((provider) => {
      try {
        provider.setUser(userId);
      } catch (error) {
        console.error(`[Analytics] ${provider.name} setUser error:`, error);
      }
    });
  }

  // Common event shortcuts
  trackFormSubmission(formId: string, formName: string): void {
    this.sendEvent('form_submission', { form_id: formId, form_name: formName });
  }

  trackFormCreated(formId: string, formName: string): void {
    this.sendEvent('form_created', { form_id: formId, form_name: formName });
  }

  /** ⚠ An account was CREATED — never `login` (P7: the sign-up page sent `login`, so GA counted no sign-ups at all). */
  trackSignUp(method: string, params?: AnalyticsEventParams): Promise<void> {
    return this.sendEventBeforeLeaving('sign_up', { method, ...params });
  }

  trackLogin(method: string): Promise<void> {
    return this.sendEventBeforeLeaving('login', { method });
  }

  trackError(errorType: string, errorMessage: string): void {
    this.sendEvent('error', {
      error_type: errorType,
      error_message: errorMessage,
    });
  }
}

export default Analytics;
